import { useState, useRef, useEffect } from 'react';
import * as Tone from 'tone';
import type { DrumInstrument, DrumsetConfig } from '../../types';
import { DRUM_TYPES } from '../../config/drumKits';
import { getSamplesByType } from '../../config/drumSamples';
import InstrumentForm from './InstrumentForm';
import ActionButton from '../common/ActionButton';
import { devLog } from '../../utils/devLog';
import { getSmartInstrumentId } from '../../utils/smartInstrumentId';
import { getSafeAudioUrl } from '../../utils/audioUtils';

interface InstrumentListProps {
  instruments: DrumInstrument[];
  setInstruments: (instruments: DrumInstrument[]) => void;
  t: (key: string) => string;
  getThemeClass: (type: string) => string;
  showDynamicMessage?: (message: string, type?: "info" | "success" | "warning" | "error") => void;
  onSave?: (config: DrumsetConfig) => void;
}

export default function InstrumentList({
  instruments,
  setInstruments,
  t,
  getThemeClass,
  showDynamicMessage,
  onSave,
}: InstrumentListProps) {
  const [playingPreviews, setPlayingPreviews] = useState<Record<string, boolean>>({});
  const lastPlayTimeRef = useRef<number>(0);
  const PLAY_THROTTLE_MS = 100;
  
  // 追焦功能：樂器移動後追焦到新位置
  const [movedInstrumentIndex, setMovedInstrumentIndex] = useState<number | null>(null);
  const instrumentRefs = useRef<(HTMLDivElement | null)[]>([]);

  // 追焦邏輯
  useEffect(() => {
    if (movedInstrumentIndex !== null && instrumentRefs.current[movedInstrumentIndex]) {
      setTimeout(() => {
        instrumentRefs.current[movedInstrumentIndex]?.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
          inline: 'nearest'
        });
        
        // 1.5秒後清除追焦狀態
        setTimeout(() => {
          setMovedInstrumentIndex(null);
        }, 1500);
      }, 100);
    }
  }, [movedInstrumentIndex]);

  const addInstrument = (preType: string = 'kick'): void => {
    const drumType = DRUM_TYPES.find(t => t.id === preType) || DRUM_TYPES[0];
    const samples = getSamplesByType(preType);
    const defaultSample = samples.length > 0 ? samples[0].id : 'kick-basic';
    const existingIds = instruments.map(inst => inst.id);
    const smartId = getSmartInstrumentId(preType as any, existingIds, process.env.NODE_ENV === 'development');
    const newInst: DrumInstrument = {
      id: smartId,
      name: t(`drumTypes.${preType}`),
      type: preType,
      sampleId: defaultSample,
      color: drumType.color,
      url: getSafeAudioUrl(defaultSample)
    };
    devLog('🆕 Adding smart instrument:', newInst);
    setInstruments([...instruments, newInst]);
  };

  const removeInstrument = (index: number): void => {
    setInstruments(instruments.filter((_, i) => i !== index));
  };

  const moveInstrumentUp = (index: number): void => {
    if (index === 0) return;
    const updated = [...instruments];
    [updated[index - 1], updated[index]] = [updated[index], updated[index - 1]];
    setInstruments(updated);
    // 設置追焦到移動後的位置（上移後位置為index-1）
    setMovedInstrumentIndex(index - 1);
  };

  const moveInstrumentDown = (index: number): void => {
    if (index === instruments.length - 1) return;
    const updated = [...instruments];
    [updated[index], updated[index + 1]] = [updated[index + 1], updated[index]];
    setInstruments(updated);
    // 設置追焦到移動後的位置（下移後位置為index+1）
    setMovedInstrumentIndex(index + 1);
  };

  const updateInstrument = (index: number, field: string, value: string): void => {
    const updated = [...instruments];
    updated[index] = { ...updated[index], [field]: value };
    
    // 如果改變了類型，自動更新相關屬性
    if (field === 'type') {
      const drumType = DRUM_TYPES.find(t => t.id === value) || DRUM_TYPES[0];
      const samples = getSamplesByType(value);
      
      // 創建一個臨時ID列表，排除當前正在編輯的樂器（避免ID衝突檢查）
      const otherIds = instruments.filter((_, i) => i !== index).map(inst => inst.id);
      
      // 自動更新ID為新類型的智能ID
      const newId = getSmartInstrumentId(value as any, otherIds, process.env.NODE_ENV === 'development');
      updated[index].id = newId;
      devLog('🔄 Auto-updated ID to:', newId);
      
      // 自動更新名稱為類型名稱
      updated[index].name = t(`drumTypes.${value}`);
      devLog('🔄 Auto-updated name to:', t(`drumTypes.${value}`));
      
      // 自動更新顏色
      updated[index].color = drumType.color;
      devLog('🔄 Auto-updated color to:', drumType.color);
      
      // 自動選擇該類型的第一個音檔
      if (samples.length > 0) {
        updated[index].sampleId = samples[0].id;
        updated[index].url = getSafeAudioUrl(samples[0].id);
        devLog('🔄 Auto-updated sample to:', samples[0].id);
      }
    }
    
    setInstruments(updated);
  };

  const previewSample = async (sampleId: string, instrumentIndex: number): Promise<void> => {
    const now = Date.now();
    if (now - lastPlayTimeRef.current < PLAY_THROTTLE_MS) return;
    lastPlayTimeRef.current = now;
    const playerId = `${sampleId}-${instrumentIndex}`;
    setPlayingPreviews(prev => ({ ...prev, [playerId]: true }));
    const audioUrl = getSafeAudioUrl(sampleId);
    
    const clearState = (): void => {
      setPlayingPreviews(prev => { 
        const updated = { ...prev }; 
        delete updated[playerId]; 
        return updated; 
      });
    };
    
    try {
      const audio = new Audio();
      audio.onended = () => clearState();
      audio.volume = 0.7;
      audio.preload = 'auto';
      audio.src = audioUrl;
      await audio.play();
    } catch {
      try {
        await Tone.start();
        const player = new Tone.Player(audioUrl).toDestination();
        player.start();
        setTimeout(() => player.dispose(), 1500);
      } catch {}
    } finally {
      setTimeout(clearState, 1500);
    }
  };

  const saveDrumKit = (): void => {
    if (instruments.length === 0) {
      if (showDynamicMessage) {
        showDynamicMessage(t('pleaseAddInstruments') || 'Please add at least one instrument', 'error');
      } else {
        alert(t('pleaseAddInstruments') || 'Please add at least one instrument');
      }
      return;
    }
    const ids = instruments.map(inst => inst.id);
    const dup = ids.filter((id, i) => ids.indexOf(id) !== i);
    if (dup.length > 0) {
      if (showDynamicMessage) {
        showDynamicMessage(`${t('duplicateIds') || 'Duplicate IDs found'}: ${dup.join(', ')}`, 'error');
      } else {
        alert(`${t('duplicateIds') || 'Duplicate IDs found'}: ${dup.join(', ')}`);
      }
      return;
    }
    const drumsetConfig: DrumsetConfig = {
      name: t('customDrumset') || 'Custom Drumset',
      instruments: instruments.map(inst => ({
        id: inst.id,
        name: inst.name,
        type: inst.type,
        sampleId: inst.sampleId,
        color: inst.color,
        url: inst.url
      })),
    };
    localStorage.setItem('drumsetConfig', JSON.stringify(drumsetConfig));
    if (onSave) onSave(drumsetConfig);
  };

  return (
    <div className={`p-4 rounded ${getThemeClass('card')} border ${getThemeClass('border')}`}> 
      {/* desktop header */}
      <div className="hidden md:flex justify-between items-center mb-4">
        <div className="flex items-center space-x-2">
          <span className="text-2xl">⚙️</span>
          <h2 className="text-lg font-semibold">{t('customKit')}</h2>
          <span className="text-sm opacity-70">({instruments.length} {t('instruments') || '樂器'})</span>
        </div>
        <div className="flex items-center space-x-2">
          <select id="newInstrumentType" className={`border px-2 py-1 rounded ${getThemeClass('border')} ${getThemeClass('select')}`} defaultValue="kick">
            {DRUM_TYPES.map(type => (
              <option key={type.id} value={type.id}>➕ {t(`drumTypes.${type.id}`)}</option>
            ))}
          </select>
          <ActionButton
            onClick={() => {
              const sel = document.getElementById('newInstrumentType') as HTMLSelectElement;
              const selectedType = sel.value;
              addInstrument(selectedType);
            }}
            icon="➕"
            text={t('addInstrument')}
            tooltip={t('addInstrument')}
            variant="primary"
          />
          <ActionButton
            onClick={saveDrumKit}
            disabled={instruments.length === 0}
            icon="💾"
            text={t('saveDrumKit')}
            tooltip={t('saveDrumKit')}
            variant="success"
          />
        </div>
      </div>

      {/* mobile header */}
      <div className="md:hidden space-y-3 mb-4">
        <div className="flex items-center space-x-2">
          <span className="text-2xl">⚙️</span>
          <h2 className="text-lg font-semibold">{t('customKit')}</h2>
          <span className="text-sm opacity-70">({instruments.length})</span>
        </div>
        <div className="flex space-x-2">
          <select id="newInstrumentTypeMobile" className={`flex-1 border px-2 py-2 rounded text-sm ${getThemeClass('border')} ${getThemeClass('select')}`} defaultValue="kick">
            {DRUM_TYPES.map(type => (
              <option key={type.id} value={type.id}>➕ {t(`drumTypes.${type.id}`)}</option>
            ))}
          </select>
          <ActionButton
            onClick={() => {
              const sel = document.getElementById('newInstrumentTypeMobile') as HTMLSelectElement;
              const selectedType = sel.value;
              addInstrument(selectedType);
            }}
            icon="➕"
            text=""
            tooltip={t('addInstrument')}
            variant="primary"
            size="small"
          />
          <ActionButton
            onClick={saveDrumKit}
            disabled={instruments.length === 0}
            icon="💾"
            text=""
            tooltip={t('saveDrumKit')}
            variant="success"
            size="small"
          />
        </div>
      </div>

      {/* instruments list */}
      <div className="space-y-4">
        {instruments.length === 0 ? (
          <div className={`p-8 rounded border-2 border-dashed ${getThemeClass('border')} text-center opacity-60`}>
            <div className="text-4xl mb-2">🥁</div>
            <p className="text-lg font-medium mb-2">{t('noInstruments')}</p>
            <p className="text-sm">{t('addFirstInstrument')}</p>
          </div>
        ) : (
          instruments.map((instrument, index) => (
            <div
              key={`${instrument.id}-${index}`}
              ref={(el) => { instrumentRefs.current[index] = el; }}
              className={`transition-all duration-500 ${
                movedInstrumentIndex === index 
                  ? 'ring-4 ring-blue-400 ring-opacity-50 scale-105' 
                  : ''
              }`}
            >
              <InstrumentForm
                instrument={instrument}
                index={index}
                instrumentsLength={instruments.length}
                updateInstrument={updateInstrument}
                removeInstrument={removeInstrument}
                moveInstrumentUp={moveInstrumentUp}
                moveInstrumentDown={moveInstrumentDown}
                previewSample={previewSample}
                playingPreviews={playingPreviews}
                getThemeClass={getThemeClass}
                t={t}
              />
            </div>
          ))
        )}
      </div>
    </div>
  );
} 